/**
 * Converts a number into a price format.
 * - Numbers less than 1,000 are displayed as standard prices (e.g., 337.50).
 * - Larger numbers are formatted with a suffix (K, M, B, T) and rounded to one decimal place (e.g., 5.3M).
 *
 * @param {number} num - The number to be converted into price format.
 * @returns {string} The formatted price string.
 */
export function abbreviateNumber(num: number): string {
    if (num < 1000) {
        return num.toFixed(2);
    }

    if (num >= 1e12) {
        return formatWithSuffix(num, 1e12, 'T');
    } else if (num >= 1e9) {
        return formatWithSuffix(num, 1e9, 'B');
    } else if (num >= 1e6) {
        return formatWithSuffix(num, 1e6, 'M');
    } else {
        return formatWithSuffix(num, 1e3, 'K');
    }
}

/**
 * Helper function to format a number with a divisor and a suffix for larger amounts.
 * It rounds the number to one decimal place.
 *
 * @param {number} num - The original number to format.
 * @param {number} divisor - The divisor corresponding to the suffix.
 * @param {string} suffix - The suffix to append (K, M, B, T).
 * @returns {string} Formatted string representation of the number with suffix.
 */
function formatWithSuffix(
    num: number,
    divisor: number,
    suffix: string
): string {
    let formattedNum = Math.round((num / divisor) * 10) / 10;
    return (
        (formattedNum % 1 === 0
            ? formattedNum.toString()
            : formattedNum.toFixed(1)) + suffix
    );
}
